import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from './cn';

const badgeVariants = cva(
  'inline-flex items-center gap-x-1 whitespace-nowrap rounded font-medium ring-1 ring-inset',
  {
    variants: {
      // `default` preserves the historical padding; `sm` is the compact size
      // for dense surfaces (table cells, inline agent cards).
      size: {
        default: 'px-2 py-1',
        sm: 'px-1.5 py-0.5 text-sm/tight',
      },
      variant: {
        default: [
          'bg-blue-50 text-blue-900 ring-blue-500/30',
          'dark:bg-blue-400/10 dark:text-blue-400 dark:ring-blue-400/30',
        ],
        neutral: [
          'bg-gray-50 text-gray-900 ring-gray-500/30',
          'dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20',
        ],
        success: [
          'bg-emerald-50 text-emerald-900 ring-emerald-600/30',
          'dark:bg-emerald-400/10 dark:text-emerald-400 dark:ring-emerald-400/20',
        ],
        error: [
          'bg-red-50 text-red-900 ring-red-600/20',
          'dark:bg-red-400/10 dark:text-red-400 dark:ring-red-400/20',
        ],
        warning: [
          'bg-yellow-50 text-yellow-900 ring-yellow-600/30',
          'dark:bg-yellow-400/10 dark:text-yellow-500 dark:ring-yellow-400/20',
        ],
        cyan: [
          'bg-cyan-50 text-cyan-900 ring-cyan-600/30',
          'dark:bg-cyan-400/10 dark:text-cyan-400 dark:ring-cyan-400/20',
        ],
        indigo: [
          'bg-indigo-50 text-indigo-900 ring-indigo-600/30',
          'dark:bg-indigo-400/10 dark:text-indigo-400 dark:ring-indigo-400/20',
        ],
      },
    },
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
  },
);

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, size, ...props }: BadgeProps) {
  return (
    <div
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
